import axios from "axios";
import { isTokenExpired, clearAuthState } from "../../utils/tokenUtils";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://riskreport.almata.co.jp:3400/api";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token refresh function
const refreshToken = async () => {
  const refreshToken = sessionStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refreshToken
    });
    
    const { accessToken, refreshToken: newRefreshToken } = response.data;
    sessionStorage.setItem("authToken", accessToken);
    sessionStorage.setItem("refreshToken", newRefreshToken);
    
    return accessToken;
  } catch (error) {
    return null;
  }
};

// Request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    const isLoginRequest = config.url?.includes("/login");
    const isRegisterRequest = config.url?.includes("/register");
    const isRefreshRequest = config.url?.includes("/refresh");
    const isResetPasswordRequest = config.url?.includes("/complete-admin-reset");
    
    // Skip token logic for auth requests
    if (isLoginRequest || isRegisterRequest || isRefreshRequest || isResetPasswordRequest) {
      return config;
    }

    let token = sessionStorage.getItem("authToken");

    if (token && isTokenExpired(token)) {
      // Try to refresh token
      token = await refreshToken();
      
      if (!token) {
        // Refresh failed -> clear all auth state & redirect
        clearAuthState();

        const currentPath = window.location.pathname;
        const publicRoutes = [
          "/cyberRiskReport",
          "/domain-report",
          "/termsofservice",
          "/login",
          "/register",
        ];
        const isPublicRoute = publicRoutes.some((route) =>
          currentPath.includes(route)
        );

        if (!isPublicRoute) {
          window.location.href = "/login";
        }

        return Promise.reject(new Error("Token expired"));
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isLoginRequest = error.config?.url?.includes("/login");
    const isRegisterRequest = error.config?.url?.includes("/register");
    const isRefreshRequest = error.config?.url?.includes("/refresh");
    const isResetPasswordRequest = error.config?.url?.includes("/complete-admin-reset");
    const originalRequest = error.config;

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !isLoginRequest &&
      !isRegisterRequest &&
      !isRefreshRequest &&
      !isResetPasswordRequest &&
      !originalRequest._retry &&
      !originalRequest?.skipAuthRedirect
    ) {
      originalRequest._retry = true;
      
      // Try to refresh token on 401/403
      const newToken = await refreshToken();
      
      if (newToken) {
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient.request(originalRequest);
      }

      // Refresh failed -> clear all auth state
      clearAuthState();

      const currentPath = window.location.pathname;
      const publicRoutes = [
        "/cyberRiskReport",
        "/domain-report",
        "/termsofservice",
      ];
      const isPublicRoute = publicRoutes.some((route) =>
        currentPath.includes(route)
      );

      if (!isPublicRoute) {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
